import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  Stack,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Check as CheckIcon,
  Schedule as ScheduleIcon,
  Phone as PhoneIcon,
  Send as SendIcon,
  Camera as CameraIcon,
  Description as DescriptionIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  Group as GroupIcon,
  Assessment as CalculatorIcon,
  Balance as BalanceIcon,
  Gavel as GavelIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as AttachMoneyIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { Claim } from '../../types/api';
import api from '../../utils/axios';

interface ClaimProgressTrackerProps {
  claim: Claim;
  onStageUpdate?: (newStage: number) => void;
}

interface ClaimStage {
  id: number;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  category?: 'initial' | 'documentation' | 'negotiation' | 'adr' | 'payment' | 'closure';
}

const ClaimProgressTracker: React.FC<ClaimProgressTrackerProps> = ({ claim, onStageUpdate }) => {
  const [currentStage, setCurrentStage] = useState<number>(1);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stages: ClaimStage[] = [
    {
      id: 1,
      name: "Initial Contact",
      description: "Client contacted, basic information collected",
      icon: PhoneIcon,
      color: "#2196f3",
      category: "initial"
    },
    {
      id: 2,
      name: "Claim Filed with Carrier",
      description: "Initial claim filed with insurance carrier",
      icon: SendIcon,
      color: "#009688",
      category: "initial"
    },
    {
      id: 3,
      name: "Property Inspection",
      description: "On-site damage assessment completed",
      icon: CameraIcon,
      color: "#9c27b0",
      category: "documentation"
    },
    {
      id: 4,
      name: "Documentation",
      description: "Photos, reports, and estimates compiled",
      icon: DescriptionIcon,
      color: "#3f51b5",
      category: "documentation"
    },
    {
      id: 5,
      name: "Claim Submission",
      description: "Complete claim package submitted to carrier",
      icon: BusinessIcon,
      color: "#ff9800",
      category: "documentation"
    },
    {
      id: 6,
      name: "Negotiation",
      description: "Working with adjuster on settlement",
      icon: WarningIcon,
      color: "#ffc107",
      category: "negotiation"
    },
    {
      id: 7,
      name: "Mediation",
      description: "Third-party mediation in progress",
      icon: GroupIcon,
      color: "#e91e63",
      category: "adr"
    },
    {
      id: 8,
      name: "Appraisal",
      description: "Independent appraisal process initiated",
      icon: CalculatorIcon,
      color: "#00bcd4",
      category: "adr"
    },
    {
      id: 9,
      name: "Arbitration",
      description: "Binding arbitration proceedings",
      icon: BalanceIcon,
      color: "#f44336",
      category: "adr"
    },
    {
      id: 10,
      name: "Litigation",
      description: "Legal action filed in court",
      icon: GavelIcon,
      color: "#607d8b",
      category: "adr"
    },
    {
      id: 11,
      name: "Settlement",
      description: "Final agreement reached",
      icon: CheckCircleIcon,
      color: "#4caf50",
      category: "payment"
    },
    {
      id: 12,
      name: "Expecting Payment",
      description: "Awaiting payment from insurance carrier",
      icon: ScheduleIcon,
      color: "#ff5722",
      category: "payment"
    },
    {
      id: 13,
      name: "Payment Received",
      description: "Claim payment received and processed",
      icon: AttachMoneyIcon,
      color: "#8bc34a",
      category: "payment"
    },
    {
      id: 14,
      name: "Claim Closed",
      description: "File closed, final documentation complete",
      icon: CloseIcon,
      color: "#9e9e9e",
      category: "closure"
    }
  ];

  useEffect(() => {
    // Initialize current stage from claim data
    // Use current_stage if available, otherwise derive from claim status
    const initialStage = (claim as any).current_stage || getStageFromClaimStatus(claim.status);
    setCurrentStage(initialStage);
  }, [claim]);

  const getStageFromClaimStatus = (status: string): number => {
    switch (status) {
      case 'OPEN': return 2; // Claim Filed with Carrier
      case 'IN_PROGRESS': return 6; // Negotiation
      case 'CLOSED': return 14; // Claim Closed
      case 'REJECTED': return 14; // Claim Closed
      default: return 1; // Initial Contact
    }
  };

  const getStageStatus = (stageId: number): 'completed' | 'active' | 'pending' => {
    if (stageId < currentStage) return 'completed';
    if (stageId === currentStage) return 'active';
    return 'pending';
  };

  const getStatusColor = (status: 'completed' | 'active' | 'pending') => {
    switch (status) {
      case 'completed': return '#4caf50';
      case 'active': return '#2196f3';
      case 'pending': return '#9e9e9e';
    }
  };

  const getCategoryChip = (category?: string) => {
    const categoryConfig = {
      initial: { label: 'Initial', color: '#2196f3' },
      documentation: { label: 'Documentation', color: '#9c27b0' },
      negotiation: { label: 'Negotiation', color: '#ff9800' },
      adr: { label: 'ADR', color: '#f44336' },
      payment: { label: 'Payment', color: '#4caf50' },
      closure: { label: 'Final', color: '#9e9e9e' },
    };

    if (!category || !categoryConfig[category as keyof typeof categoryConfig]) return null;
    
    const config = categoryConfig[category as keyof typeof categoryConfig];
    return (
      <Chip 
        label={config.label} 
        size="small" 
        sx={{ 
          backgroundColor: `${config.color}20`,
          color: config.color,
          fontWeight: 'bold',
          fontSize: '0.7rem'
        }} 
      />
    );
  };

  const progressPercentage = ((currentStage - 1) / (stages.length - 1)) * 100;

  const handleStageUpdate = async (newStage: number) => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    setError(null);
    
    try {
      // Make API call to update the claim stage
      const response = await api.put(`/claims/${claim.file_number}/stage`, { 
        stage: newStage 
      });
      
      if (response.data.success) {
        setCurrentStage(newStage);
        onStageUpdate?.(newStage);
        
        // Show success message briefly
        setTimeout(() => {
          // Could add a success toast here if you have a toast system
        }, 100);
      } else {
        throw new Error(response.data.message || 'Failed to update stage');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update claim stage';
      setError(errorMessage);
      console.error('Error updating stage:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box sx={{ maxWidth: '100%', p: 3 }}>
      {/* Claim Header */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
          border: '1px solid #e1f5fe'
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
              {claim.file_number}
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {claim.insuredName}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">Property:</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {claim.claimAddress || claim.location || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">Loss Type:</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {claim.type}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">Date Opened:</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(claim.dateReported).toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4} textAlign="right">
            <Typography variant="body2" color="text.secondary">Claim Value</Typography>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              ${claim.amount.toLocaleString()}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Overall Progress */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            Claim Progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(progressPercentage)}% Complete
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progressPercentage} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #2196f3 0%, #4caf50 100%)',
              borderRadius: 4,
            }
          }} 
        />
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ADR Notice */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Claims may not follow all stages sequentially. Some stages (Mediation, Appraisal, Arbitration, Litigation) are alternative dispute resolution paths used when initial negotiation is unsuccessful.
        </Typography>
      </Alert>

      {/* Detailed Stage Progress */}
      <Box>
        {stages.map((stage, index) => {
          const status = getStageStatus(stage.id);
          const IconComponent = stage.icon;
          const isLast = index === stages.length - 1;
          
          return (
            <Box key={stage.id} sx={{ position: 'relative', mb: 3 }}>
              {/* Connector Line */}
              {!isLast && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 24,
                    top: 48,
                    width: 2,
                    height: 60,
                    backgroundColor: status === 'completed' ? '#4caf50' : '#e0e0e0',
                    transition: 'background-color 0.3s ease',
                  }}
                />
              )}
              
              <Card 
                elevation={status === 'active' ? 4 : 1}
                sx={{ 
                  border: status === 'active' ? '2px solid #2196f3' : '1px solid #e0e0e0',
                  backgroundColor: status === 'completed' ? '#f1f8e9' : status === 'active' ? '#e3f2fd' : '#fafafa',
                  transition: 'all 0.3s ease',
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    {/* Stage Icon */}
                    <Avatar
                      sx={{
                        backgroundColor: getStatusColor(status),
                        color: 'white',
                        width: 48,
                        height: 48,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {status === 'completed' ? (
                        <CheckIcon />
                      ) : status === 'active' ? (
                        <ScheduleIcon />
                      ) : (
                        <IconComponent />
                      )}
                    </Avatar>

                    {/* Stage Content */}
                    <Box flex={1}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <Typography 
                          variant="h6" 
                          fontWeight="bold"
                          color={status === 'completed' ? 'success.main' : status === 'active' ? 'primary.main' : 'text.secondary'}
                        >
                          {stage.name}
                        </Typography>
                        {getCategoryChip(stage.category)}
                      </Stack>
                      <Typography 
                        variant="body2" 
                        color={status === 'pending' ? 'text.disabled' : 'text.secondary'}
                      >
                        {stage.description}
                      </Typography>
                    </Box>

                    {/* Status Indicator */}
                    <Box textAlign="right">
                      {status === 'completed' && (
                        <Chip 
                          label="Completed" 
                          color="success" 
                          size="small" 
                          variant="filled"
                        />
                      )}
                      {status === 'active' && (
                        <Chip 
                          label="In Progress" 
                          color="primary" 
                          size="small" 
                          variant="filled"
                          sx={{ animation: 'pulse 2s infinite' }}
                        />
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>

      {/* Action Buttons */}
      <Paper elevation={1} sx={{ p: 3, mt: 3 }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
          <Button 
            variant="outlined" 
            startIcon={<VisibilityIcon />}
            onClick={() => {/* Handle view documents */}}
          >
            View Documents
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<EditIcon />}
            disabled={isUpdating}
            onClick={() => {/* Handle update status */}}
          >
            Update Status
          </Button>
          <Button 
            variant="contained" 
            startIcon={<EmailIcon />}
            onClick={() => {/* Handle contact client */}}
          >
            Contact Client
          </Button>
        </Stack>
      </Paper>

      {/* Demo Controls - Remove in production */}
      <Paper elevation={1} sx={{ p: 3, mt: 3, backgroundColor: '#f5f5f5' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Demo Controls (Remove in Production):
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          {stages.map((stage) => (
            <Button
              key={stage.id}
              size="small"
              variant={currentStage === stage.id ? "contained" : "outlined"}
              onClick={() => handleStageUpdate(stage.id)}
              disabled={isUpdating}
              sx={{ fontSize: '0.7rem', minWidth: 'auto' }}
            >
              {stage.name}
            </Button>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};

export default ClaimProgressTracker;
